import type {
  FertilityProjectionResult,
  ObservedValue,
  ProjectionEstimate,
  ProjectionInput,
  ProjectionModelMetadata,
  ProjectionPresentation,
  ProjectionStage,
  ProjectionStatus,
  RateEstimate,
  RetrievalComparisonEvidence,
  RetrievalOutcomeInput,
  ProjectionUncertainty,
  SemenSampleInput,
} from "./types";
import type { ProjectionModel } from "./model";

const CONTRACT_VERSION = "1" as const;

const MOON_SOURCE_ID = "moon-oocyte-yield-2016";
const MATURITY_SOURCE_ID = "bergin-planned-oc-maturity-2026";
const AGE_SOURCE_ID = "machtinger-age-oocyte-yield-2025";
const COBO_ELECTIVE_FP_SOURCE_ID = "cobo-oocyte-cryopreservation-2016";
const CASCANTE_SOURCE_ID = "cascante-planned-oocyte-cryopreservation-2024";
const GOLDMAN_SOURCE_ID = "goldman-oocyte-cryopreservation-2017";
const ASRM_EMBRYO_SOURCE_ID = "asrm-embryo-vitrification-2021";
const TUMMON_EMBRYO_OUTCOME_SOURCE_ID =
  "tummon-frozen-thawed-embryo-transfer-2006";
const ARIBARG_SPERM_SOURCE_ID = "aribarg-sperm-post-thaw-1995";
const VERZE_SPERM_SOURCE_ID = "verze-sperm-cryopreservation-2013";
const JIANG_SPERM_SOURCE_ID = "jiang-sperm-bank-freezability-2017";
const MARCHIANI_SPERM_SOURCE_ID = "marchiani-sperm-cryopreservation-2023";
const ASRM_SEMEN_SOURCE_ID = "asrm-semen-lab-guidance-2022";

export const RETRIEVAL_MODEL_METADATA: ProjectionModelMetadata = {
  id: "moon-oocyte-yield-2016-oocyte-yield",
  version: "1.0.0",
  reviewStatus: "pending_clinical_review",
  population:
    "141 IVF stimulation cycles at Seoul National University Hospital; PCOS and chronic disease were excluded",
  validationScope:
    "Source-backed ovarian response equations; requires validation in planned oocyte cryopreservation and local laboratory populations",
  sourceIds: [
    MOON_SOURCE_ID,
    "tighe-planned-oocyte-yield-2025",
    MATURITY_SOURCE_ID,
    AGE_SOURCE_ID,
    COBO_ELECTIVE_FP_SOURCE_ID,
  ],
  parameterSourceIds: [
    MOON_SOURCE_ID,
    MATURITY_SOURCE_ID,
    AGE_SOURCE_ID,
    COBO_ELECTIVE_FP_SOURCE_ID,
  ],
  supportedFeatureIds: [
    "age",
    "amh",
    "antral_follicle_count",
    "fsh",
    "cycle_counts",
  ],
  supportedGoalKinds: ["expected_stage_count"],
  priority: 30,
  estimateClass: "personalized",
};

export const PLANNED_OC_OUTCOME_MODEL_METADATA: ProjectionModelMetadata = {
  id: "cascante-2024-planned-oocyte-clbr",
  version: "1.0.0",
  reviewStatus: "pending_clinical_review",
  population:
    "731 patients who thawed autologous oocytes after planned oocyte cryopreservation at one university-affiliated center",
  validationScope:
    "Source-backed age-by-total-M2 table for cumulative live birth or ongoing pregnancy; local and prospective validation required",
  sourceIds: [CASCANTE_SOURCE_ID],
  parameterSourceIds: [CASCANTE_SOURCE_ID],
  supportedFeatureIds: ["age", "oocyte_inventory", "cycle_counts"],
  supportedGoalKinds: [
    "probability_at_least_one_live_birth",
    "expected_stage_count",
  ],
  priority: 40,
  estimateClass: "personalized",
};

export const GENERAL_OOCYTE_OUTCOME_MODEL_METADATA: ProjectionModelMetadata = {
  id: "goldman-2017-general-oocyte-outcome",
  version: "1.0.0",
  reviewStatus: "pending_clinical_review",
  population:
    "Goldman et al. model derived from 520 fresh autologous ICSI cycles with uncompromised ovarian reserve and an external euploidy dataset",
  validationScope:
    "General counseling baseline for mature oocyte cohorts; clinic-specific thaw, blastulation, euploidy, and transfer outcomes should replace these parameters when available",
  sourceIds: [GOLDMAN_SOURCE_ID],
  parameterSourceIds: [GOLDMAN_SOURCE_ID],
  supportedFeatureIds: ["age", "oocyte_inventory", "cycle_counts"],
  supportedGoalKinds: [
    "probability_at_least_one_live_birth",
    "expected_stage_count",
  ],
  priority: 20,
  estimateClass: "general_baseline",
};

export const GENERAL_EMBRYO_TRANSFER_MODEL_METADATA: ProjectionModelMetadata = {
  id: "general-frozen-embryo-transfer-baseline",
  version: "1.0.0",
  reviewStatus: "pending_clinical_review",
  population:
    "Broad baseline assembled from an ASRM vitrification committee opinion and a long-term frozen-thawed embryo transfer cohort",
  validationScope:
    "General planning baseline for stored embryo cohorts; embryo stage, grade, PGT status, uterine factors, and clinic-specific FET outcomes should replace these parameters when available",
  sourceIds: [ASRM_EMBRYO_SOURCE_ID, TUMMON_EMBRYO_OUTCOME_SOURCE_ID],
  parameterSourceIds: [ASRM_EMBRYO_SOURCE_ID, TUMMON_EMBRYO_OUTCOME_SOURCE_ID],
  supportedFeatureIds: ["age", "embryo_inventory", "cycle_counts"],
  supportedGoalKinds: [
    "probability_at_least_one_live_birth",
    "expected_stage_count",
  ],
  priority: 20,
  estimateClass: "general_baseline",
};

export const GENERAL_SEMEN_CRYOSURVIVAL_MODEL_METADATA: ProjectionModelMetadata =
  {
    id: "marchiani-2023-general-semen-cryosurvival",
    version: "1.0.0",
    reviewStatus: "pending_clinical_review",
    population:
      "Human sperm-bank collections, including patients banking for oligospermia, cancer, and other conditions, with a separate donor subgroup",
    validationScope:
      "General cryosurvival baseline for post-thaw motility and viability recovery; laboratory thaw protocol, sperm preparation, and patient-specific freezability validation must supersede these rates",
    sourceIds: [
      ARIBARG_SPERM_SOURCE_ID,
      VERZE_SPERM_SOURCE_ID,
      JIANG_SPERM_SOURCE_ID,
      MARCHIANI_SPERM_SOURCE_ID,
      ASRM_SEMEN_SOURCE_ID,
    ],
    parameterSourceIds: [
      ARIBARG_SPERM_SOURCE_ID,
      VERZE_SPERM_SOURCE_ID,
      JIANG_SPERM_SOURCE_ID,
      MARCHIANI_SPERM_SOURCE_ID,
    ],
    supportedFeatureIds: ["semen"],
    supportedGoalKinds: ["expected_stage_count"],
    priority: 30,
    estimateClass: "general_baseline",
  };

type AgeBand = "under35" | "35to37" | "38to40" | "41plus";
type M2Band = "1to9" | "10to14" | "15to19" | "20plus";

type ClbrCell = {
  mean: number;
  low: number;
  high: number;
};

const CLBR_TABLE: Record<AgeBand, Record<M2Band, ClbrCell>> = {
  under35: {
    "1to9": { mean: 0.33, low: 0.13, high: 0.59 },
    "10to14": { mean: 0.35, low: 0.17, high: 0.56 },
    "15to19": { mean: 0.64, low: 0.43, high: 0.82 },
    "20plus": { mean: 0.71, low: 0.52, high: 0.86 },
  },
  "35to37": {
    "1to9": { mean: 0.32, low: 0.21, high: 0.44 },
    "10to14": { mean: 0.53, low: 0.41, high: 0.64 },
    "15to19": { mean: 0.45, low: 0.29, high: 0.62 },
    "20plus": { mean: 0.78, low: 0.67, high: 0.87 },
  },
  "38to40": {
    "1to9": { mean: 0.21, low: 0.14, high: 0.29 },
    "10to14": { mean: 0.44, low: 0.32, high: 0.56 },
    "15to19": { mean: 0.49, low: 0.34, high: 0.64 },
    "20plus": { mean: 0.53, low: 0.38, high: 0.68 },
  },
  "41plus": {
    "1to9": { mean: 0.1, low: 0.03, high: 0.23 },
    "10to14": { mean: 0.29, low: 0.1, high: 0.56 },
    "15to19": { mean: 0.38, low: 0.09, high: 0.76 },
    "20plus": { mean: 0.36, low: 0.13, high: 0.65 },
  },
};

export const RETRIEVAL_MODEL: ProjectionModel = {
  metadata: RETRIEVAL_MODEL_METADATA,
  project(input, now = new Date()) {
    const generatedAt = now.toISOString();
    const age = validAge(input.patient.ageAtRetrieval);
    const amh = validPositive(input.patient.amhNgMl);
    const afc = validCount(input.patient.antralFollicleCount);
    const fsh = validPositive(input.patient.fshIuL);
    const measured = validCount(input.cycle?.retrievedOocytes);
    const provided = validCount(input.cycle?.expectedRetrievedOocytes);
    const stages: ProjectionStage[] = [];
    const missingInputs: string[] = [];
    let expected: ProjectionEstimate | null = null;
    const invalidRetrievalCount =
      (input.cycle?.retrievedOocytes && measured == null) ||
      (input.cycle?.expectedRetrievedOocytes && provided == null);
    if (invalidRetrievalCount) {
      missingInputs.push("valid retrieved oocyte count");
    }
    if (
      input.cycle?.priorRetrievals?.some((retrieval) =>
        [
          retrieval.retrievedOocytes,
          retrieval.matureOocytes,
          retrieval.vitrifiedOocytes,
        ].some((value) => value != null && validCount(value) == null),
      )
    ) {
      missingInputs.push("valid prior retrieval outcome counts");
    }
    const ageOnly = age != null && amh == null ? ageOnlyYield(age) : null;

    if (measured != null) {
      stages.push(
        observedStage(
          "retrieved_oocytes",
          input.cycle?.retrievedOocytes,
          measured,
        ),
      );
    } else if (provided != null) {
      stages.push(
        providedStage(
          "retrieved_oocytes",
          input.cycle?.expectedRetrievedOocytes,
          provided,
        ),
      );
    } else {
      expected =
        ageOnly?.retrieved ?? estimateRetrievedOocytes(age, amh, afc, fsh);
      if (expected) {
        stages.push({
          id: "retrieved_oocytes",
          basis: "modeled",
          estimate: expected,
          sourceIds: [ageOnly ? ageOnly.sourceId : MOON_SOURCE_ID],
        });
      } else {
        missingInputs.push(
          ...missingRetrievalInputs(
            age,
            amh,
            afc,
            Boolean(input.patient.amhNgMl),
          ),
        );
      }
    }

    const matureObserved = validCount(input.cycle?.matureOocytes);
    const vitrifiedObserved = validCount(input.cycle?.vitrifiedOocytes);
    const retrievedReference = measured ?? provided;
    const mature =
      matureObserved != null &&
      (retrievedReference == null || matureObserved <= retrievedReference)
        ? matureObserved
        : null;
    const vitrified =
      vitrifiedObserved != null &&
      (matureObserved == null || vitrifiedObserved <= matureObserved)
        ? vitrifiedObserved
        : null;
    if (input.cycle?.matureOocytes && matureObserved == null) {
      missingInputs.push("valid mature oocyte count");
    } else if (matureObserved != null && mature == null) {
      missingInputs.push(
        "mature oocyte count cannot exceed retrieved oocyte count",
      );
    }
    if (input.cycle?.vitrifiedOocytes && vitrifiedObserved == null) {
      missingInputs.push("valid vitrified oocyte count");
    } else if (vitrifiedObserved != null && vitrified == null) {
      missingInputs.push(
        "vitrified oocyte count cannot exceed mature oocyte count",
      );
    }
    if (mature != null) {
      stages.push(
        observedStage("mature_oocytes", input.cycle?.matureOocytes, mature),
      );
    } else if (ageOnly) {
      stages.push({
        id: "mature_oocytes",
        basis: "modeled",
        estimate: ageOnly.mature,
        sourceIds: [ageOnly.sourceId],
      });
    } else if (stages[0]) {
      stages.push({
        id: "mature_oocytes",
        basis: "modeled",
        estimate: multiplyByRate(stages[0].estimate, 0.74),
        rate: {
          mean: 0.74,
          low: 0.74,
          high: 0.74,
          unit: "probability",
          sourceIds: [MATURITY_SOURCE_ID],
        },
        sourceIds: [MATURITY_SOURCE_ID],
      });
    }
    if (vitrified != null) {
      stages.push(
        observedStage(
          "vitrified_oocytes",
          input.cycle?.vitrifiedOocytes,
          vitrified,
        ),
      );
    }

    const modeled = stages.some((stage) => stage.basis === "modeled");
    const status = projectionStatus(stages, missingInputs, modeled);
    return {
      ...commonResult(
        input,
        RETRIEVAL_MODEL_METADATA,
        generatedAt,
        stages,
        missingInputs,
      ),
      status,
      presentation: modeledPresentation(RETRIEVAL_MODEL_METADATA, modeled),
      priorRetrievalComparisons: buildRetrievalComparisons(
        input.cycle?.priorRetrievals,
      ),
      assumptions: [
        "The Moon et al. equation predicts retrieved oocytes after controlled ovarian stimulation, not live birth.",
        "When AMH is absent, the age-only stage estimates use Machtinger et al.'s 25th, 50th, and 75th percentiles for ages 30-41 and Cobo et al.'s >40 elective-FP aggregate for ages 42-44. Outside 30-44 no age-only estimate is issued.",
        "The modeled retrieval-to-mature transition uses the 74.0% overall maturity rate reported by Bergin et al.; it is not age- or protocol-specific.",
        "The source cohort excluded PCOS and chronic disease and was not a planned-cryopreservation cohort.",
        "The published equation does not provide an individual prediction interval; low and high equal the point estimate until local calibration supplies one.",
        ...(input.cycle?.priorRetrievals?.length
          ? [
              "Prior retrieval outcomes are retained as observed comparison evidence and are not silently pooled into the current-cycle forecast.",
            ]
          : []),
      ],
      warnings: modeled
        ? [
            "This ovarian-response adapter is source-backed but pending clinical review and validation in planned oocyte cryopreservation.",
          ]
        : [],
      uncertainty: pointEstimateUncertainty(
        "The source publication reports model coefficients but not a patient-level prediction interval in the projection contract.",
      ),
      expectedLiveBirths: null,
      probabilityAtLeastOneLiveBirth: null,
      disclaimer: projectionDisclaimer(),
    };
  },
};

export const PLANNED_OC_OUTCOME_MODEL: ProjectionModel = {
  metadata: PLANNED_OC_OUTCOME_MODEL_METADATA,
  project(input, now = new Date()) {
    const generatedAt = now.toISOString();
    const age = validAge(input.patient.ageAtRetrieval);
    const thawed = validCount(input.oocytes?.thawed);
    const stages: ProjectionStage[] = [];
    const missingInputs: string[] = [];

    const frozen = validCount(input.oocytes?.frozen);
    let baseline: FertilityProjectionResult | null = null;
    if (frozen != null) {
      stages.push(
        observedStage("frozen_oocytes", input.oocytes?.frozen, frozen),
      );
    }
    if (thawed != null) {
      stages.push(
        observedStage("thawed_oocytes", input.oocytes?.thawed, thawed),
      );
    } else {
      missingInputs.push("total mature oocytes thawed");
    }

    for (const [id, observed] of [
      ["fertilized_oocytes", input.oocytes?.fertilized],
      ["blastocysts", input.oocytes?.blastocysts],
    ] as const) {
      const value = validCount(observed);
      if (value != null) stages.push(observedStage(id, observed, value));
    }

    // Cascante remains the preferred live-birth probability model when a
    // thawed count is known. Goldman supplies the labeled general baseline
    // for downstream stage counts that the planned-OC cohort did not report.
    if (thawed != null) {
      const baselineFrozen = input.oocytes?.frozen ?? {
        value: thawed,
        provenance: {
          sourceType: "model_input" as const,
          sourceId: PLANNED_OC_OUTCOME_MODEL_METADATA.id,
          note: "Thawed mature-oocyte count used as the baseline cohort when the stored count is absent.",
        },
      };
      baseline = GENERAL_OOCYTE_OUTCOME_MODEL.project(
        {
          ...input,
          kind: "frozen_oocyte_cohort",
          oocytes: { ...input.oocytes, frozen: baselineFrozen },
        },
        now,
      );
      baseline.stages.forEach((stage) => {
        if (stage.id === "frozen_oocytes" && frozen == null) return;
        if (!stages.some((current) => current.id === stage.id)) {
          stages.push(stage);
        }
      });
    }

    const cell =
      age != null && thawed != null && thawed > 0
        ? clbrCell(age, thawed)
        : null;
    if (age == null && input.patient.ageAtRetrieval) {
      missingInputs.push("valid patient age at oocyte retrieval");
    }
    const probability =
      input.oocytes?.fertilized ||
      input.oocytes?.blastocysts ||
      input.embryos?.euploid
        ? (baseline?.probabilityAtLeastOneLiveBirth ?? null)
        : age != null && thawed === 0
          ? exactProbability(0)
          : cell
            ? estimate(cell)
            : null;
    const modeled =
      probability != null || stages.some((stage) => stage.basis === "modeled");
    const status = projectionStatus(stages, missingInputs, modeled);
    return {
      ...commonResult(
        input,
        PLANNED_OC_OUTCOME_MODEL_METADATA,
        generatedAt,
        stages,
        missingInputs,
      ),
      status,
      presentation: modeledPresentation(
        PLANNED_OC_OUTCOME_MODEL_METADATA,
        modeled,
      ),
      assumptions: [
        "The target is cumulative live birth or ongoing pregnancy per patient, based on the total number of mature oocytes thawed.",
        "Cycle count is not applied because it was not an independent predictor in the source model; its benefit is represented through the number of mature oocytes.",
        "The source cohort excluded medical-indication cryopreservation and was from one university-affiliated center.",
        "Goldman et al. supplies a general, source-backed baseline for intermediate stage counts when clinic observations are absent; Cascante remains the probability model for planned-OC cumulative live birth.",
      ],
      warnings: modeled
        ? [
            "This planned-cryopreservation outcome adapter is source-backed but pending clinical review, transportability assessment, and local calibration.",
          ]
        : [],
      uncertainty: {
        kind: "interval",
        method: "provided-by-model",
        confidenceLevel: 0.95,
        note: "Low and high values are the 95% confidence limits reported for the age-by-M2 cell in Cascante et al.; they are not individual prediction intervals.",
      },
      expectedLiveBirths: null,
      probabilityAtLeastOneLiveBirth: probability,
      disclaimer: projectionDisclaimer(),
    };
  },
};

/**
 * General counseling baseline for a mature-oocyte cohort.
 *
 * Goldman et al. published a complete age-based counseling model rather than
 * a clinic-specific transition table: assumed thaw survival, age-dependent
 * blastocyst formation, age-dependent euploidy, and 60% live birth per
 * transferred euploid blastocyst. This is intentionally a general estimate;
 * a reviewed clinic rate set or patient observations should supersede it.
 */
export const GENERAL_OOCYTE_OUTCOME_MODEL: ProjectionModel = {
  metadata: GENERAL_OOCYTE_OUTCOME_MODEL_METADATA,
  project(input, now = new Date()) {
    const generatedAt = now.toISOString();
    const age = validAge(input.patient.ageAtRetrieval);
    const frozen = validCount(input.oocytes?.frozen);
    const stages: ProjectionStage[] = [];
    const missingInputs: string[] = [];

    if (frozen == null) {
      missingInputs.push("frozen mature oocyte count");
    } else {
      stages.push(
        observedStage("frozen_oocytes", input.oocytes?.frozen, frozen),
      );
    }

    const survival = goldmanThawSurvival(age);
    const fertilization = rateEstimate(0.73, 0.73, 0.73);
    const blastulation = goldmanBlastulationRate(age);
    const euploidy = goldmanEuploidyRate(age);
    const liveBirthGivenEuploid = rateEstimate(0.6, 0.6, 0.6);

    const thawed = validCount(input.oocytes?.thawed);
    if (thawed != null) {
      stages.push(
        observedStage("thawed_oocytes", input.oocytes?.thawed, thawed),
      );
    } else if (frozen != null) {
      stages.push(
        modeledStage(
          "thawed_oocytes",
          multiplyCount(exactCount(frozen), survival),
          survival,
        ),
      );
    }

    const fertilized = validCount(input.oocytes?.fertilized);
    if (fertilized != null) {
      stages.push(
        observedStage(
          "fertilized_oocytes",
          input.oocytes?.fertilized,
          fertilized,
        ),
      );
    } else if (stages.some((stage) => stage.id === "thawed_oocytes")) {
      stages.push(
        modeledStage(
          "fertilized_oocytes",
          multiplyCount(stages.at(-1)!.estimate, fertilization),
          fertilization,
        ),
      );
    }

    const blastocysts = validCount(input.oocytes?.blastocysts);
    if (blastocysts != null) {
      stages.push(
        observedStage("blastocysts", input.oocytes?.blastocysts, blastocysts),
      );
    } else if (fertilized != null || thawed != null || frozen != null) {
      const blastocystBase =
        fertilized != null
          ? exactCount(fertilized)
          : thawed != null
            ? exactCount(thawed)
            : exactCount(frozen!);
      const blastocystRate =
        fertilized != null
          ? conditionalBlastulationRate(
              blastulation,
              rateProduct(survival, fertilization),
            )
          : thawed != null
            ? conditionalBlastulationRate(blastulation, survival)
            : blastulation;
      stages.push(
        modeledStage(
          "blastocysts",
          multiplyCount(blastocystBase, blastocystRate),
          blastocystRate,
        ),
      );
    }

    const euploid = validCount(input.embryos?.euploid);
    if (euploid != null) {
      stages.push(
        observedStage("euploid_embryos", input.embryos?.euploid, euploid),
      );
    } else if (stages.some((stage) => stage.id === "blastocysts")) {
      stages.push(
        modeledStage(
          "euploid_embryos",
          multiplyCount(stages.at(-1)!.estimate, euploidy),
          euploidy,
        ),
      );
    }

    const euploidStage = stages.find((stage) => stage.id === "euploid_embryos");
    if (euploidStage) {
      stages.push(
        modeledStage(
          "live_births",
          multiplyCount(euploidStage.estimate, liveBirthGivenEuploid),
          liveBirthGivenEuploid,
        ),
      );
    }

    if (age == null) {
      missingInputs.push(
        "patient age for age-stratified euploidy and live-birth probability",
      );
    }

    const liveBirths = stages.find((stage) => stage.id === "live_births");
    const observedBlastocysts =
      blastocysts != null ? exactCount(blastocysts) : null;
    const observedFertilized =
      fertilized != null ? exactCount(fertilized) : null;
    const observedThawed = thawed != null ? exactCount(thawed) : null;
    const probability =
      liveBirths &&
      (observedBlastocysts ||
        observedFertilized ||
        observedThawed ||
        frozen != null)
        ? probabilityFromObservedBase(
            frozen,
            observedThawed,
            observedFertilized,
            observedBlastocysts,
            blastulation,
            survival,
            fertilization,
            euploidy,
            liveBirthGivenEuploid,
          )
        : null;
    const modeled = stages.some((stage) => stage.basis === "modeled");

    return {
      ...commonResult(
        input,
        GENERAL_OOCYTE_OUTCOME_MODEL_METADATA,
        generatedAt,
        stages,
        missingInputs,
      ),
      status: projectionStatus(stages, missingInputs, modeled),
      presentation: modeledPresentation(
        GENERAL_OOCYTE_OUTCOME_MODEL_METADATA,
        modeled,
      ),
      assumptions: [
        "This is a general counseling baseline, not a clinic-specific or patient-specific guarantee.",
        "Goldman et al. assumed 95% thaw survival below age 36 and 85% at age 36 or older.",
        "The Goldman cohort's average fertilization rate was 73%; the source assumed fresh-cycle fertilization and blastulation were reasonable proxies for frozen-thawed use.",
        "Blastocyst formation follows Goldman's age-dependent mature-oocyte-to-blastocyst equation, which already includes thaw survival; thaw survival is not multiplied into that stage a second time.",
        "Euploidy follows the source's age trend; age is required for an individualized live-birth estimate.",
        "The model assumes 60% live birth per transferred euploid blastocyst; clinics should replace this with their own reviewed outcome data.",
        ...(age == null
          ? [
              "Age was not supplied, so intermediate stages use a broad source-derived range and live-birth probability remains unstratified.",
            ]
          : []),
      ],
      warnings: modeled
        ? [
            "This general baseline is source-backed but pending clinical review and local calibration; it should be labeled as a general estimate in patient-facing UI.",
          ]
        : [],
      uncertainty: pointEstimateUncertainty(
        "The published source provides cohort assumptions and age trends, not a patient-specific confidence interval for every intermediate stage.",
      ),
      expectedLiveBirths: liveBirths?.estimate ?? null,
      probabilityAtLeastOneLiveBirth: probability,
      disclaimer: projectionDisclaimer(),
    };
  },
};

/**
 * General planning baseline for stored embryos when no clinic-reviewed FET
 * outcome set or embryo-specific observations are available.
 */
export const GENERAL_EMBRYO_TRANSFER_MODEL: ProjectionModel = {
  metadata: GENERAL_EMBRYO_TRANSFER_MODEL_METADATA,
  project(input, now = new Date()) {
    const generatedAt = now.toISOString();
    const frozen = validCount(input.embryos?.frozen);
    const warmed = validCount(input.embryos?.warmed);
    const transferred = validCount(input.embryos?.transferred);
    const stages: ProjectionStage[] = [];
    const missingInputs: string[] = [];
    const thawSurvival = rateEstimate(0.95, 0.9, 0.99, [
      ASRM_EMBRYO_SOURCE_ID,
      TUMMON_EMBRYO_OUTCOME_SOURCE_ID,
    ]);
    const implantation = rateEstimate(0.22, 0.15, 0.3, [
      TUMMON_EMBRYO_OUTCOME_SOURCE_ID,
    ]);
    const liveBirthPerTransfer = rateEstimate(0.18, 0.12, 0.25, [
      TUMMON_EMBRYO_OUTCOME_SOURCE_ID,
    ]);

    if (frozen == null) {
      missingInputs.push("frozen embryo count");
    } else {
      stages.push(
        observedStage("frozen_embryos", input.embryos?.frozen, frozen),
      );
    }

    if (warmed != null) {
      stages.push(
        observedStage("warmed_embryos", input.embryos?.warmed, warmed),
      );
    } else if (frozen != null) {
      stages.push(
        modeledStage(
          "warmed_embryos",
          multiplyCount(exactCount(frozen), thawSurvival),
          thawSurvival,
        ),
      );
    }

    const transferBase =
      transferred != null
        ? observedStage(
            "transferred_embryos",
            input.embryos?.transferred,
            transferred,
          )
        : stages.find((stage) => stage.id === "warmed_embryos")
          ? modeledStage(
              "transferred_embryos",
              stages.find((stage) => stage.id === "warmed_embryos")!.estimate,
              rateEstimate(1, 1, 1, [ASRM_EMBRYO_SOURCE_ID]),
            )
          : null;
    if (transferBase) stages.push(transferBase);

    const transferStage = stages.find(
      (stage) => stage.id === "transferred_embryos",
    );
    if (transferStage) {
      stages.push(
        modeledStage(
          "implanted_embryos",
          multiplyCount(transferStage.estimate, implantation),
          implantation,
        ),
      );
      stages.push(
        modeledStage(
          "live_births",
          multiplyCount(transferStage.estimate, liveBirthPerTransfer),
          liveBirthPerTransfer,
        ),
      );
    }

    const modeled = stages.some((stage) => stage.basis === "modeled");
    const probability = transferStage
      ? probabilityFromCount(transferStage.estimate.mean, liveBirthPerTransfer)
      : null;
    return {
      ...commonResult(
        input,
        GENERAL_EMBRYO_TRANSFER_MODEL_METADATA,
        generatedAt,
        stages,
        missingInputs,
      ),
      status: projectionStatus(stages, missingInputs, modeled),
      presentation: modeledPresentation(
        GENERAL_EMBRYO_TRANSFER_MODEL_METADATA,
        modeled,
      ),
      assumptions: [
        "This is a general planning baseline, not a clinic-specific or patient-specific guarantee.",
        "The thaw-survival range combines a 95% historical frozen-thawed cohort result with ASRM's statement that modern embryo vitrification survival approaches 100%.",
        "The implantation and live-birth rates come from a 2006 cleavage-stage frozen-thawed transfer cohort and may not represent modern blastocyst, euploid, or vitrification outcomes.",
        "Stored embryos are treated as available for transfer; embryo grade, PGT result, endometrial factors, and transfer strategy are not applied without reviewed model parameters.",
      ],
      warnings: modeled
        ? [
            "This general embryo baseline is source-backed but pending clinical review and local calibration; label it as a general estimate in patient-facing UI.",
          ]
        : [],
      uncertainty: pointEstimateUncertainty(
        "Ranges are broad source-derived planning bands, not patient-specific confidence intervals.",
      ),
      expectedLiveBirths:
        stages.find((stage) => stage.id === "live_births")?.estimate ?? null,
      probabilityAtLeastOneLiveBirth: probability,
      disclaimer: projectionDisclaimer(),
    };
  },
};

/**
 * General sperm cryosurvival baseline.
 *
 * Marchiani et al. report recovery ratios for motility and viability rather
 * than a pregnancy endpoint. Aribarg, Verze, and Jiang provide the rationale
 * for using pre-freeze motility and concentration as freezability inputs, but
 * none of these publications is a validated individual-patient calculator.
 * The adapter therefore exposes recovery metrics only and never infers a live
 * birth probability from them.
 */
export const GENERAL_SEMEN_CRYOSURVIVAL_MODEL: ProjectionModel = {
  metadata: GENERAL_SEMEN_CRYOSURVIVAL_MODEL_METADATA,
  project(input, now = new Date()) {
    const generatedAt = now.toISOString();
    const stages: ProjectionStage[] = [];
    const missingInputs: string[] = [];
    const inventory = input.semenInventory;
    const samples = input.semen ?? [];
    const sample = samples[0];

    addObservedCountStage(stages, "semen_samples", inventory?.collectedSamples);
    addObservedCountStage(
      stages,
      "processed_semen",
      inventory?.processedSamples,
    );
    addObservedCountStage(stages, "frozen_vials", inventory?.frozenVials);

    if (!sample && !inventory) {
      missingInputs.push("semen inventory or a pre-freeze semen sample");
    }

    const concentration = averageObservedValue(
      samples,
      (item) => item.concentrationMillionPerMl,
      validPositive,
    );
    const progressiveMotility = averageObservedValue(
      samples,
      (item) => item.progressiveMotilityPercent,
      validPercentage,
    );
    const totalMotility = averageObservedValue(
      samples,
      (item) => item.totalMotilityPercent,
      validPercentage,
    );
    const viability = averageObservedValue(
      samples,
      (item) => item.viabilityPercent,
      validPercentage,
    );
    const source =
      samples.length > 0 &&
      samples.every((item) => item.source?.value === "donor")
        ? "donor"
        : "partner";
    const donor = source === "donor";
    const lowConcentration = concentration != null && concentration < 2;
    const rates = lowConcentration
      ? {
          motility: rateEstimate(0.01, 0.01, 0.02, [
            MARCHIANI_SPERM_SOURCE_ID,
            VERZE_SPERM_SOURCE_ID,
          ]),
          viability: rateEstimate(0.05, 0.03, 0.08, [
            MARCHIANI_SPERM_SOURCE_ID,
            VERZE_SPERM_SOURCE_ID,
          ]),
        }
      : donor
        ? {
            motility: rateEstimate(0.615, 0.55, 0.68, [
              MARCHIANI_SPERM_SOURCE_ID,
            ]),
            viability: rateEstimate(0.638, 0.58, 0.7, [
              MARCHIANI_SPERM_SOURCE_ID,
            ]),
          }
        : {
            motility: rateEstimate(0.119, 0.08, 0.16, [
              MARCHIANI_SPERM_SOURCE_ID,
              ARIBARG_SPERM_SOURCE_ID,
              VERZE_SPERM_SOURCE_ID,
              JIANG_SPERM_SOURCE_ID,
            ]),
            viability: rateEstimate(0.411, 0.32, 0.5, [
              MARCHIANI_SPERM_SOURCE_ID,
            ]),
          };

    const observedPostThawMotility = averageObservedValue(
      samples,
      (item) => item.postThawProgressiveMotilityPercent,
      validPercentage,
    );
    if (observedPostThawMotility != null) {
      stages.push(
        observedPercentageStage(
          "post_thaw_progressive_motility_percent",
          firstObserved(
            samples,
            (item) => item.postThawProgressiveMotilityPercent,
          ),
          observedPostThawMotility,
        ),
      );
    } else if (progressiveMotility != null) {
      stages.push(
        modeledPercentageStage(
          "post_thaw_progressive_motility_percent",
          multiplyPercentage(progressiveMotility, rates.motility),
          rates.motility,
        ),
      );
    } else if (totalMotility != null) {
      stages.push(
        modeledPercentageStage(
          "post_thaw_total_motility_percent",
          multiplyPercentage(totalMotility, rates.motility),
          rates.motility,
        ),
      );
    }

    const observedPostThawViability = averageObservedValue(
      samples,
      (item) => item.postThawViabilityPercent,
      validPercentage,
    );
    if (observedPostThawViability != null) {
      stages.push(
        observedPercentageStage(
          "post_thaw_viability_percent",
          firstObserved(samples, (item) => item.postThawViabilityPercent),
          observedPostThawViability,
        ),
      );
    } else if (viability != null) {
      stages.push(
        modeledPercentageStage(
          "post_thaw_viability_percent",
          multiplyPercentage(viability, rates.viability),
          rates.viability,
        ),
      );
    }

    if (
      !stages.some(
        (stage) => stage.id === "post_thaw_motility_recovery_percent",
      )
    ) {
      stages.push(
        modeledPercentageStage(
          "post_thaw_motility_recovery_percent",
          percentageEstimateFromRate(rates.motility),
          rates.motility,
        ),
      );
    }
    if (!stages.some((stage) => stage.id === "post_thaw_viability_percent")) {
      stages.push(
        modeledPercentageStage(
          "post_thaw_viability_recovery_percent",
          percentageEstimateFromRate(rates.viability),
          rates.viability,
        ),
      );
    }

    const modeled = stages.some((stage) => stage.basis === "modeled");
    const concentrationAssumption = lowConcentration
      ? "The sample concentration is below 2 million/mL; the low-concentration subgroup in Marchiani et al. had near-zero motility recovery, so a conservative near-zero floor is used."
      : "When concentration is available, it is used only for the low-concentration warning band; the published studies do not support a universal patient-level concentration multiplier.";
    return {
      ...commonResult(
        input,
        GENERAL_SEMEN_CRYOSURVIVAL_MODEL_METADATA,
        generatedAt,
        stages,
        missingInputs,
      ),
      status: projectionStatus(stages, missingInputs, modeled),
      presentation: modeledPresentation(
        GENERAL_SEMEN_CRYOSURVIVAL_MODEL_METADATA,
        modeled,
      ),
      assumptions: [
        "Motility and viability recovery are population-level post-thaw recovery metrics, not a prediction of fertilization, pregnancy, or live birth.",
        "The general partner-sample baseline uses Marchiani et al.'s reported median recovery of 11.9% for motility and 41.1% for viability; donor samples use the reported donor medians of 61.5% and 63.8%.",
        "Aribarg, Verze, and Jiang support using pre-freeze motility and concentration as freezability context, but they do not provide a universally validated individual calculator.",
        "A laboratory's thaw protocol, processing method, sperm preparation, and observed post-thaw results should replace this general baseline whenever available.",
        concentrationAssumption,
        ...(samples.some((item) => item.morphologyPercent)
          ? [
              "Morphology was recorded but is not applied to cryosurvival because the cited cryosurvival studies do not establish a transportable morphology coefficient for this endpoint.",
            ]
          : []),
        ...(samples.some((item) => item.dnaFragmentationPercent)
          ? [
              "DNA fragmentation was recorded but is not applied to cryosurvival in this adapter.",
            ]
          : []),
      ],
      warnings: modeled
        ? [
            "This sperm cryosurvival baseline is source-backed but pending clinical review and clinic-specific calibration; label it as a general estimate.",
          ]
        : [],
      uncertainty: pointEstimateUncertainty(
        "Published recovery values are cohort summaries; the displayed interval is a planning band and not a patient-specific prediction interval.",
      ),
      expectedLiveBirths: null,
      probabilityAtLeastOneLiveBirth: null,
      disclaimer: projectionDisclaimer(),
    };
  },
};

function estimateRetrievedOocytes(
  age: number | null,
  amh: number | null,
  afc: number | null,
  fsh: number | null,
): ProjectionEstimate | null {
  if (age == null || amh == null) return null;
  let mean: number;
  if (afc != null && fsh != null) {
    mean = Math.exp(
      2.584 - 0.015 * age - 0.035 * fsh + 0.038 * amh + 0.026 * afc,
    );
  } else if (afc != null) {
    mean = Math.exp(2.32 - 0.017 * age + 0.039 * amh + 0.03 * afc);
  } else if (fsh != null) {
    mean = Math.exp(3.422 - 0.03 * age - 0.049 * fsh + 0.08 * amh);
  } else {
    mean = Math.exp(3.21 - 0.036 * age + 0.089 * amh);
  }
  const bounded = Math.max(0, mean);
  return { mean: bounded, low: bounded, high: bounded, unit: "count" };
}

type AgeOnlyYield = {
  retrieved: ProjectionEstimate;
  mature: ProjectionEstimate;
  sourceId: string;
};

const AGE_ONLY_YIELD: Record<
  number,
  {
    retrieved: [number, number, number];
    mature: [number, number, number];
    sourceId: string;
  }
> = {
  30: {
    retrieved: [16, 20, 24],
    mature: [12, 15, 20],
    sourceId: AGE_SOURCE_ID,
  },
  31: {
    retrieved: [13, 17, 22],
    mature: [10, 13, 17],
    sourceId: AGE_SOURCE_ID,
  },
  32: { retrieved: [11, 15, 21], mature: [8, 12, 16], sourceId: AGE_SOURCE_ID },
  33: { retrieved: [11, 15, 21], mature: [8, 11, 16], sourceId: AGE_SOURCE_ID },
  34: { retrieved: [10, 15, 22], mature: [8, 11, 16], sourceId: AGE_SOURCE_ID },
  35: { retrieved: [10, 14, 21], mature: [7, 11, 16], sourceId: AGE_SOURCE_ID },
  36: { retrieved: [8, 13, 19], mature: [6, 10, 15], sourceId: AGE_SOURCE_ID },
  37: { retrieved: [7, 11, 17], mature: [5, 8, 13], sourceId: AGE_SOURCE_ID },
  38: { retrieved: [6, 10, 16], mature: [5, 8, 12], sourceId: AGE_SOURCE_ID },
  39: { retrieved: [6, 9, 15], mature: [4, 7, 12], sourceId: AGE_SOURCE_ID },
  40: { retrieved: [5, 9, 14], mature: [4, 6, 11], sourceId: AGE_SOURCE_ID },
  41: { retrieved: [5, 8, 13], mature: [3, 6, 11], sourceId: AGE_SOURCE_ID },
  42: {
    retrieved: [4, 5, 6],
    mature: [3, 4, 5],
    sourceId: COBO_ELECTIVE_FP_SOURCE_ID,
  },
  43: {
    retrieved: [4, 5, 6],
    mature: [3, 4, 5],
    sourceId: COBO_ELECTIVE_FP_SOURCE_ID,
  },
  44: {
    retrieved: [4, 5, 6],
    mature: [3, 4, 5],
    sourceId: COBO_ELECTIVE_FP_SOURCE_ID,
  },
};

const AGE_ONLY_LOWER_BOUND = 30;
const AGE_ONLY_UPPER_BOUND = 44;

function ageOnlyYield(age: number): AgeOnlyYield | null {
  if (age < AGE_ONLY_LOWER_BOUND || age > AGE_ONLY_UPPER_BOUND) return null;
  const lowerAge = Math.floor(age);
  const upperAge = Math.ceil(age);
  const lower = AGE_ONLY_YIELD[lowerAge];
  const upper = AGE_ONLY_YIELD[upperAge];
  if (!lower || !upper) return null;
  const fraction = age - lowerAge;
  return {
    retrieved: percentileEstimate(
      interpolateTriplet(lower.retrieved, upper.retrieved, fraction),
    ),
    mature: percentileEstimate(
      interpolateTriplet(lower.mature, upper.mature, fraction),
    ),
    sourceId: fraction < 0.5 ? lower.sourceId : upper.sourceId,
  };
}

function interpolateTriplet(
  lower: [number, number, number],
  upper: [number, number, number],
  fraction: number,
): [number, number, number] {
  return lower.map(
    (value, index) => value + (upper[index]! - value) * fraction,
  ) as [number, number, number];
}

function percentileEstimate(
  values: [number, number, number],
): ProjectionEstimate {
  return { low: values[0], mean: values[1], high: values[2], unit: "count" };
}

function missingRetrievalInputs(
  age: number | null,
  amh: number | null,
  afc: number | null,
  amhWasSupplied: boolean,
): string[] {
  return [
    ...(age == null ? ["patient age at the retrieval step"] : []),
    ...(amh == null && amhWasSupplied ? ["valid AMH in ng/mL"] : []),
    ...(amh == null && !amhWasSupplied && age != null
      ? ["a patient age between 30 and 44 for the age-only nomogram"]
      : []),
    ...(afc == null ? [] : []),
  ];
}

function clbrCell(age: number, thawed: number): ClbrCell {
  const ageBand: AgeBand =
    age < 35
      ? "under35"
      : age <= 37
        ? "35to37"
        : age <= 40
          ? "38to40"
          : "41plus";
  const m2Band: M2Band =
    thawed <= 9
      ? "1to9"
      : thawed <= 14
        ? "10to14"
        : thawed <= 19
          ? "15to19"
          : "20plus";
  return CLBR_TABLE[ageBand][m2Band];
}

function estimate(cell: ClbrCell): ProjectionEstimate {
  return { ...cell, unit: "probability" };
}

function exactProbability(value: number): ProjectionEstimate {
  return { mean: value, low: value, high: value, unit: "probability" };
}

function rateEstimate(
  mean: number,
  low: number,
  high: number,
  sourceIds: string[] = [GOLDMAN_SOURCE_ID],
): RateEstimate {
  return {
    mean: clamp(mean, 0, 1),
    low: clamp(Math.min(low, mean), 0, 1),
    high: clamp(Math.max(high, mean), 0, 1),
    unit: "probability",
    sourceIds,
  };
}

function modeledStage(
  id: string,
  estimate: ProjectionEstimate,
  transitionRate: RateEstimate,
): ProjectionStage {
  return {
    id,
    basis: "modeled",
    estimate,
    rate: transitionRate,
    sourceIds: transitionRate.sourceIds,
  };
}

function observedPercentageStage(
  id: string,
  observed: ObservedValue<number> | undefined,
  value: number,
): ProjectionStage {
  return {
    id,
    basis: "observed",
    observed,
    estimate: {
      mean: value,
      low: value,
      high: value,
      unit: "percentage",
    },
    sourceIds: observed?.provenance.sourceId
      ? [observed.provenance.sourceId]
      : [],
  };
}

function modeledPercentageStage(
  id: string,
  estimate: ProjectionEstimate,
  transitionRate: RateEstimate,
): ProjectionStage {
  return {
    id,
    basis: "modeled",
    estimate,
    rate: transitionRate,
    sourceIds: transitionRate.sourceIds,
  };
}

function percentageEstimateFromRate(rate: RateEstimate): ProjectionEstimate {
  return {
    mean: rate.mean * 100,
    low: rate.low * 100,
    high: rate.high * 100,
    unit: "percentage",
  };
}

function multiplyPercentage(
  baseline: number,
  recoveryRate: RateEstimate,
): ProjectionEstimate {
  return {
    mean: baseline * recoveryRate.mean,
    low: baseline * recoveryRate.low,
    high: baseline * recoveryRate.high,
    unit: "percentage",
  };
}

function multiplyCount(
  count: ProjectionEstimate,
  transitionRate: RateEstimate,
): ProjectionEstimate {
  return {
    mean: count.mean * transitionRate.mean,
    low: count.low * transitionRate.low,
    high: count.high * transitionRate.high,
    unit: "count",
  };
}

function goldmanThawSurvival(age: number | null): RateEstimate {
  if (age == null) return rateEstimate(0.9, 0.85, 0.95);
  const survival = age < 36 ? 0.95 : 0.85;
  return rateEstimate(survival, survival, survival);
}

function goldmanBlastulationRate(age: number | null): RateEstimate {
  if (age == null) {
    return rateEstimate(
      goldmanBlastocystProbability(39.5),
      goldmanBlastocystProbability(44),
      goldmanBlastocystProbability(35),
    );
  }
  const probability = goldmanBlastocystProbability(age);
  return rateEstimate(probability, probability, probability);
}

function goldmanBlastocystProbability(age: number): number {
  const thawSurvival = age < 36 ? 0.95 : 0.85;
  return thawSurvival * Math.exp(2.8043 - 0.1112 * age);
}

function goldmanEuploidyRate(age: number | null): RateEstimate {
  if (age == null) return rateEstimate(0.375, 0.127, 0.574);

  let probability: number;
  if (age <= 35) {
    probability = 0.574;
  } else if (age <= 37) {
    probability = interpolate(0.574, 0.486, age, 35, 37);
  } else if (age <= 44) {
    probability = interpolate(0.486, 0.127, age, 37, 44);
  } else {
    probability = 0.127;
  }
  return rateEstimate(probability, probability, probability);
}

function interpolate(
  lower: number,
  upper: number,
  value: number,
  lowerBound: number,
  upperBound: number,
): number {
  const fraction = (value - lowerBound) / (upperBound - lowerBound);
  return lower + (upper - lower) * Math.max(0, Math.min(1, fraction));
}

function conditionalBlastulationRate(
  matureToBlastocyst: RateEstimate,
  upstreamRate: RateEstimate,
): RateEstimate {
  return rateEstimate(
    matureToBlastocyst.mean / upstreamRate.mean,
    matureToBlastocyst.low / upstreamRate.high,
    matureToBlastocyst.high / upstreamRate.low,
    [...new Set([...matureToBlastocyst.sourceIds, ...upstreamRate.sourceIds])],
  );
}

function rateProduct(...rates: RateEstimate[]): RateEstimate {
  return rateEstimate(
    rates.reduce((product, item) => product * item.mean, 1),
    rates.reduce((product, item) => product * item.low, 1),
    rates.reduce((product, item) => product * item.high, 1),
    [...new Set(rates.flatMap((item) => item.sourceIds))],
  );
}

function probabilityFromObservedBase(
  frozen: number | null,
  thawed: ProjectionEstimate | null,
  fertilized: ProjectionEstimate | null,
  blastocysts: ProjectionEstimate | null,
  matureToBlastocyst: RateEstimate,
  survival: RateEstimate,
  fertilization: RateEstimate,
  euploidy: RateEstimate,
  liveBirthGivenEuploid: RateEstimate,
): ProjectionEstimate | null {
  if (blastocysts) {
    return probabilityFromCount(
      blastocysts.mean,
      rateProduct(euploidy, liveBirthGivenEuploid),
    );
  }
  if (fertilized) {
    return probabilityFromCount(
      fertilized.mean,
      rateProduct(
        conditionalBlastulationRate(
          matureToBlastocyst,
          rateProduct(survival, fertilization),
        ),
        euploidy,
        liveBirthGivenEuploid,
      ),
    );
  }
  if (thawed) {
    return probabilityFromCount(
      thawed.mean,
      rateProduct(
        conditionalBlastulationRate(matureToBlastocyst, survival),
        euploidy,
        liveBirthGivenEuploid,
      ),
    );
  }
  if (frozen != null) {
    return probabilityFromCount(
      frozen,
      rateProduct(matureToBlastocyst, euploidy, liveBirthGivenEuploid),
    );
  }
  return null;
}

function probabilityFromCount(
  count: number,
  perItemRate: RateEstimate,
): ProjectionEstimate {
  const atLeastOne = (rate: number) =>
    clamp(1 - Math.pow(1 - rate, count), 0, 1);
  return {
    mean: atLeastOne(perItemRate.mean),
    low: atLeastOne(perItemRate.low),
    high: atLeastOne(perItemRate.high),
    unit: "probability",
  };
}

function clamp(value: number, low: number, high: number): number {
  return Math.min(high, Math.max(low, value));
}

function commonResult(
  input: ProjectionInput,
  metadata: ProjectionModelMetadata,
  generatedAt: string,
  stages: ProjectionStage[],
  missingInputs: string[],
): Omit<
  FertilityProjectionResult,
  | "status"
  | "presentation"
  | "assumptions"
  | "warnings"
  | "uncertainty"
  | "expectedLiveBirths"
  | "probabilityAtLeastOneLiveBirth"
  | "disclaimer"
> {
  const normalizedInputs: Record<string, number | string | null> = {
    kind: input.kind,
    ageAtRetrieval: input.patient.ageAtRetrieval?.value ?? null,
    amhNgMl: input.patient.amhNgMl?.value ?? null,
    antralFollicleCount: input.patient.antralFollicleCount?.value ?? null,
    fshIuL: input.patient.fshIuL?.value ?? null,
    thawedOocytes: input.oocytes?.thawed?.value ?? null,
    frozenEmbryos: input.embryos?.frozen?.value ?? null,
    warmedEmbryos: input.embryos?.warmed?.value ?? null,
    transferredEmbryos: input.embryos?.transferred?.value ?? null,
    euploidEmbryos: input.embryos?.euploid?.value ?? null,
    semenSamples: input.semenInventory?.collectedSamples?.value ?? null,
    processedSemen: input.semenInventory?.processedSamples?.value ?? null,
    frozenVials: input.semenInventory?.frozenVials?.value ?? null,
    semenConcentration:
      input.semen?.[0]?.concentrationMillionPerMl?.value ?? null,
    semenProgressiveMotility:
      input.semen?.[0]?.progressiveMotilityPercent?.value ?? null,
    semenViability: input.semen?.[0]?.viabilityPercent?.value ?? null,
    priorRetrievalCount: input.cycle?.priorRetrievals?.length ?? 0,
    priorRetrievedOocytes:
      input.cycle?.priorRetrievals?.reduce(
        (total, retrieval) =>
          total + (validCount(retrieval.retrievedOocytes) ?? 0),
        0,
      ) ?? 0,
  };
  const inputSourceIds = [
    input.patient.ageAtRetrieval,
    input.patient.amhNgMl,
    input.patient.antralFollicleCount,
    input.patient.fshIuL,
    input.oocytes?.frozen,
    input.oocytes?.thawed,
    input.oocytes?.fertilized,
    input.oocytes?.blastocysts,
    input.embryos?.frozen,
    input.embryos?.warmed,
    input.embryos?.transferred,
    input.embryos?.euploid,
    input.cycle?.retrievedOocytes,
    input.cycle?.expectedRetrievedOocytes,
    input.cycle?.matureOocytes,
    input.cycle?.vitrifiedOocytes,
    ...(input.cycle?.priorRetrievals?.flatMap((retrieval) => [
      retrieval.retrievedOocytes,
      retrieval.matureOocytes,
      retrieval.vitrifiedOocytes,
    ]) ?? []),
    input.semenInventory?.collectedSamples,
    input.semenInventory?.processedSamples,
    input.semenInventory?.frozenVials,
    input.semen?.[0]?.volumeMl,
    input.semen?.[0]?.concentrationMillionPerMl,
    input.semen?.[0]?.totalMotilityPercent,
    input.semen?.[0]?.progressiveMotilityPercent,
    input.semen?.[0]?.viabilityPercent,
    input.semen?.[0]?.morphologyPercent,
    input.semen?.[0]?.dnaFragmentationPercent,
    input.semen?.[0]?.postThawProgressiveMotilityPercent,
    input.semen?.[0]?.postThawViabilityPercent,
    input.semen?.[0]?.source,
    input.semen?.[0]?.fertilizationMethod,
  ].flatMap((value) =>
    value?.provenance.sourceId ? [value.provenance.sourceId] : [],
  );
  return {
    contractVersion: CONTRACT_VERSION,
    kind: input.kind,
    model: metadata,
    generatedAt,
    asOf: input.asOf ?? null,
    normalizedInputs,
    missingInputs: [...new Set(missingInputs)],
    stages,
    sourceIds: [
      ...new Set([
        ...metadata.sourceIds,
        ...inputSourceIds,
        ...stages.flatMap((stage) => stage.sourceIds),
      ]),
    ],
  };
}

function buildRetrievalComparisons(
  retrievals: RetrievalOutcomeInput[] | undefined,
): RetrievalComparisonEvidence[] {
  if (!Array.isArray(retrievals)) return [];
  return retrievals.map((retrieval) => {
    const values = [
      retrieval.retrievedOocytes,
      retrieval.matureOocytes,
      retrieval.vitrifiedOocytes,
    ];
    const normalized = values.map((value) => validCount(value));
    const hasInvalid = values.some(
      (value, index) => value != null && normalized[index] == null,
    );
    const present = normalized.filter((value) => value != null).length;
    return {
      cycleId: retrieval.cycleId,
      retrievedOocytes: normalized[0] ?? null,
      matureOocytes: normalized[1] ?? null,
      vitrifiedOocytes: normalized[2] ?? null,
      status: hasInvalid ? "invalid" : present === 3 ? "valid" : "partial",
      sourceIds: values.flatMap((value) =>
        value?.provenance.sourceId ? [value.provenance.sourceId] : [],
      ),
    };
  });
}

function addObservedCountStage(
  stages: ProjectionStage[],
  id: string,
  observed: ObservedValue<number> | undefined,
): void {
  const value = validCount(observed);
  if (value != null) stages.push(observedStage(id, observed, value));
}

function observedStage(
  id: string,
  observed: ObservedValue<number> | undefined,
  value: number,
): ProjectionStage {
  return {
    id,
    basis: "observed",
    observed,
    estimate: exactCount(value),
    sourceIds: observed?.provenance.sourceId
      ? [observed.provenance.sourceId]
      : [],
  };
}

function providedStage(
  id: string,
  observed: ObservedValue<number> | undefined,
  value: number,
): ProjectionStage {
  return {
    id,
    basis: "provided_projection",
    observed,
    estimate: exactCount(value),
    sourceIds: observed?.provenance.sourceId
      ? [observed.provenance.sourceId]
      : [],
  };
}

function exactCount(value: number): ProjectionEstimate {
  return { mean: value, low: value, high: value, unit: "count" };
}

function multiplyByRate(
  count: ProjectionEstimate,
  rate: number,
): ProjectionEstimate {
  return {
    mean: count.mean * rate,
    low: count.low * rate,
    high: count.high * rate,
    unit: "count",
  };
}

function validAge(observed: ObservedValue<number> | undefined): number | null {
  return observed &&
    Number.isFinite(observed.value) &&
    observed.value >= 18 &&
    observed.value <= 60
    ? observed.value
    : null;
}

function validPositive(
  observed: ObservedValue<number> | undefined,
): number | null {
  return observed && Number.isFinite(observed.value) && observed.value > 0
    ? observed.value
    : null;
}

function validPercentage(
  observed: ObservedValue<number> | undefined,
): number | null {
  return observed &&
    Number.isFinite(observed.value) &&
    observed.value >= 0 &&
    observed.value <= 100
    ? observed.value
    : null;
}

function averageObservedValue(
  samples: SemenSampleInput[],
  selector: (sample: SemenSampleInput) => ObservedValue<number> | undefined,
  validator: (observed: ObservedValue<number> | undefined) => number | null,
): number | null {
  const values = samples
    .map((sample) => validator(selector(sample)))
    .filter((value): value is number => value != null);
  return values.length > 0
    ? values.reduce((sum, value) => sum + value, 0) / values.length
    : null;
}

function firstObserved(
  samples: SemenSampleInput[],
  selector: (sample: SemenSampleInput) => ObservedValue<number> | undefined,
): ObservedValue<number> | undefined {
  const sample = samples.find((candidate) => selector(candidate));
  return sample ? selector(sample) : undefined;
}

function validCount(
  observed: ObservedValue<number> | undefined,
): number | null {
  return observed &&
    Number.isFinite(observed.value) &&
    Number.isInteger(observed.value) &&
    observed.value >= 0
    ? observed.value
    : null;
}

function projectionStatus(
  stages: ProjectionStage[],
  missingInputs: string[],
  modeled: boolean,
): ProjectionStatus {
  if (stages.length === 0) return "insufficient_data";
  if (missingInputs.length > 0) return "partial";
  return modeled ? "not_reviewed" : "available";
}

function modeledPresentation(
  metadata: ProjectionModelMetadata,
  modeled: boolean,
): ProjectionPresentation {
  if (!modeled) return "patient_safe";
  if (metadata.estimateClass === "general_baseline") {
    return "general_baseline";
  }
  return metadata.reviewStatus === "pending_clinical_review"
    ? "blocked_pending_review"
    : "patient_safe";
}

function pointEstimateUncertainty(note: string): ProjectionUncertainty {
  return { kind: "interval", method: "provided-by-model", note };
}

function projectionDisclaimer(): string {
  return "This is a population-level estimate, not a diagnosis, treatment recommendation, or guarantee of pregnancy or live birth.";
}
